import { Fragment, type ReactNode } from 'react';
import { animated } from '@react-spring/web';
import clsx from 'clsx';
import { useMedia } from 'react-use';
import { PricingNudge } from '@hungryroot/components-order-summary';
import { CaretDownNew, Truck } from '@hungryroot/icons';
import { Price, Skeleton } from '@hungryroot/pho';

import { Paragraph, Heading } from '@hungryroot/ramen';
import { PromoType } from '@hungryroot/rest/api/get_pricing_config/get';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  PromoCodeContainer,
  PromoLineItem,
} from '../PromoLineItem/PromoLineItem';
import styles from './OrderSummary.module.css';

interface OrderLineItem {
  label: string;
  value: number;
  id: string;
  code: string;
  type: PromoType;
}

export interface PromoValue {
  value?: null | number;
  isExpired: boolean;
  type: PromoType;
  label: string;
  code: string;
  threshold: number;
}

interface Props {
  testid?: string;
  subtotal: number;
  promos?: PromoValue[];
  amountSummary: ReactNode;
  containerClassName?: string;
  footerContainerClassName?: string;
  shipping: number;
  credit?: number;
  orderId?: number;
  children: ReactNode;
  setCollapsed?: () => void;
  shippingVisibleRef?: (node?: Element | null | undefined) => void;
  shippingFreeVisibleRef?: (node?: Element | null | undefined) => void;
  childrenRef?: (node?: Element | null | undefined) => void;
  spring?: object;
  hidePromo?: boolean;
  totalText: string;
  showHorizontalLine?: boolean;
  summaryText?: string;
  isLoading?: boolean;
  cyoPromoCode?: string | null;
  subscriptions?: ReactNode;
  flexCreditMessaging?: boolean;
  showPricingNudge?: boolean;
  subtotalLabel?: string;
  freeShippingDelta?: string | null;
}

const getCYOPromoText = (CYOPromoCode: string) => {
  switch (CYOPromoCode) {
    case 'CYOCOOKIEDOUGH':
      return 'Cookie dough for life';
    case 'CYOPROTEIN':
      return 'Protein for life';
    case 'CYOVEGGIES':
      return 'Veggies for life';
    default:
      return;
  }
};

export const PromoTypeWithDollarEffect = new Set([
  PromoType.FLAT_DISCOUNT,
  PromoType.PERCENT_DISCOUNT,
  PromoType.REFERRAL_DISCOUNT,
]);

export const getNumPromosWithDollarEffect = (
  subtotal: number,
  promos?: PromoValue[]
) => {
  return (
    promos?.reduce((acc, promo) => {
      const { threshold, isExpired, type } = promo;
      if (
        PromoTypeWithDollarEffect.has(promo.type) &&
        isPromoValid(Number(threshold), isExpired, subtotal, type)
      ) {
        return acc + 1;
      }
      return acc;
    }, 0) || 0
  );
};

const isPromoValid = (
  threshold: number,
  isExpired: boolean,
  subtotal: number,
  type: PromoType
) => {
  return !isExpired && type in PromoType && subtotal >= threshold;
};

export function OrderSummary(props: Props) {
  const {
    testid,
    subtotal,
    promos,
    amountSummary,
    containerClassName,
    shipping,
    credit,
    orderId,
    children,
    setCollapsed,
    shippingVisibleRef,
    shippingFreeVisibleRef,
    footerContainerClassName,
    spring,
    hidePromo,
    totalText,
    showHorizontalLine,
    summaryText = 'Order summary',
    childrenRef,
    isLoading,
    cyoPromoCode,
    subscriptions,
    flexCreditMessaging,
    showPricingNudge,
    subtotalLabel,
    freeShippingDelta,
  } = props;

  const isDesktop = useMedia(BreakpointQueryLarge);

  const lineItems: OrderLineItem[] | undefined = promos?.reduce(
    (acc, promo) => {
      const { value, isExpired, type, label, code, threshold } = promo;
      // skip if expired or type isn't in one of our supported promo types
      if (
        !isPromoValid(threshold, isExpired, subtotal, type) ||
        (!hidePromo && type !== PromoType.FREE_SHIPPING)
      ) {
        return acc;
      }
      return [
        ...acc,
        {
          type,
          label,
          value: value || 0,
          code,
          subtotal,
          id: label,
        },
      ] as OrderLineItem[];
    },
    [] as OrderLineItem[]
  );

  const promoCodeInput =
    (promos && getNumPromosWithDollarEffect(subtotal, promos) > 0) ||
    !hidePromo ? null : (
      <div key="promo-code-empty" className={styles.row}>
        <PromoCodeContainer
          orderId={orderId}
          subtotal={subtotal}
          showPricingNudge={showPricingNudge}
        />
      </div>
    );

  const cyoPromoText =
    cyoPromoCode !== null && cyoPromoCode !== undefined
      ? getCYOPromoText(cyoPromoCode)
      : undefined;

  return (
    <div>
      <animated.div
        data-testid={testid}
        className={`${styles.container} ${containerClassName}`}
        style={!isDesktop && spring ? spring : {}}
      >
        <div className={styles['container-inner']}>
          <div className={styles['header-container']}>
            <Heading variant="H4">{summaryText}</Heading>
            {setCollapsed && (
              <button className={styles.caret} onClick={setCollapsed}>
                <CaretDownNew />
              </button>
            )}
          </div>
          <div>
            <div className={styles.row}>
              <Paragraph>
                {subtotalLabel}
                {flexCreditMessaging && <sup>*</sup>}
              </Paragraph>
              <Paragraph testid={`${testid}-subtotal`}>
                {isLoading ? (
                  <Skeleton className={styles.line} />
                ) : (
                  <Price amount={subtotal} />
                )}
              </Paragraph>
            </div>
          </div>
          {cyoPromoText !== undefined && (
            <div className={styles.row}>
              <Paragraph>{cyoPromoText}</Paragraph>
              <Paragraph className={styles.free}>Free</Paragraph>
            </div>
          )}
          {lineItems?.length === 0 && shipping === 0
            ? promoCodeInput
            : lineItems?.map(({ label, value, id, code, type }) => {
                return (
                  <Fragment key={id}>
                    {/* if there's no promo applied, show the promo code input above shipping */}
                    {type === PromoType.FREE_SHIPPING &&
                      orderId !== undefined &&
                      promoCodeInput}
                    <div
                      ref={
                        type === PromoType.FREE_SHIPPING
                          ? shippingFreeVisibleRef
                          : null
                      }
                      className={styles.row}
                    >
                      <PromoLineItem
                        orderId={orderId}
                        value={value}
                        label={label}
                        code={code}
                        type={type}
                        subtotal={subtotal}
                      />
                    </div>
                  </Fragment>
                );
              })}
          {/* if there's no promo applied, show the promo code input above shipping */}
          {shipping > 0 ? promoCodeInput : null}
          {shipping > 0 ? (
            <div>
              <div
                ref={shippingVisibleRef}
                className={clsx({
                  [styles.row]: true,
                  [styles['row-with-promo']]: freeShippingDelta !== null,
                })}
              >
                <Paragraph>Shipping</Paragraph>
                <Paragraph>
                  <Price testid={testid} amount={shipping} />
                </Paragraph>
              </div>
              {freeShippingDelta !== null && (
                <div className={styles.nudge}>
                  <PricingNudge icon={<Truck />}>
                    You're ${freeShippingDelta} away from free shipping!
                  </PricingNudge>
                </div>
              )}
            </div>
          ) : null}
          {credit && credit > 0 ? (
            <div className={styles.row}>
              <Paragraph>Credit</Paragraph>
              <Paragraph className={styles.credit}>
                <Price testid={testid} amount={credit * -1} />
              </Paragraph>
            </div>
          ) : null}
          {showHorizontalLine && <hr className={styles['horizontal-line']} />}
          <div className={styles['total-container']}>
            <Heading variant="H5">{totalText}</Heading>
            {amountSummary}
          </div>
          <Paragraph variant="caption" className={styles.note}>
            Plus applicable state and local taxes
          </Paragraph>
        </div>
        {subscriptions && (
          <div className={styles.subscriptions}>{subscriptions}</div>
        )}
      </animated.div>
      <div ref={childrenRef} className={footerContainerClassName}>
        {children}
      </div>
    </div>
  );
}
